import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Check, ChevronDown, Lock } from 'lucide-react'
import { useFilingRailState } from '../contexts/FilingRailContext.jsx'
import './FilingRail.css'

/**
 * The filing flow's left rail, shown in place of the app sidebar on /new-patent/*.
 *
 * Filing is a focus mode: one column of navigation, and it is the steps of the filing rather
 * than the rest of the product. Two left columns — the platform nav and a step list beside it —
 * would crowd the form and split the eye. The logo and "Save & exit" are the way back out.
 *
 * Phases follow the real route order. A phase with parts can be expanded to show them, the way
 * Corgi's rail does; the current phase opens by itself. Only the intake's parts are live — the
 * page publishes them through FilingRailContext so they can be revisited from here. The parts
 * of later phases are the sections of those pages, listed so the road ahead is visible.
 */
const PHASES = [
  { id: 1, label: 'Your invention' },
  { id: 2, label: 'Originality check' },
  { id: 3, label: 'Draft with AI', parts: ['Specification', 'Figures'] },
  { id: 4, label: 'Review' },
  { id: 5, label: 'Filing details', parts: ['Contact information', 'Company information', 'Other inventors'] },
  { id: 6, label: 'File with the USPTO', parts: ['Your filing documents', 'Patent Center account', 'Final steps'] },
]

/** The phase a filing route belongs to. The intake page overrides this for its last step. */
function phaseForPath(pathname) {
  if (pathname.startsWith('/new-patent/step-2-1') || pathname.startsWith('/new-patent/step-2-2')) return 3
  if (pathname.startsWith('/new-patent/review')) return 4
  if (pathname.startsWith('/new-patent/file')) return 5
  if (pathname.startsWith('/new-patent/step-3') || pathname.startsWith('/new-patent/step-4')) return 6
  return 1
}

export default function FilingRail() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const rail = useFilingRailState()
  const phase = rail?.phase ?? phaseForPath(pathname)

  // Which groups are open. The current phase opens itself whenever it changes; the rest stay
  // however the person left them.
  const [open, setOpen] = useState(() => new Set([phase]))
  useEffect(() => {
    setOpen((prev) => (prev.has(phase) ? prev : new Set(prev).add(phase)))
  }, [phase])
  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <aside className="filing-rail" aria-label="Filing progress">
      <div className="filing-rail-head">
        <button type="button" className="filing-rail-brand" onClick={() => navigate('/dashboard')} aria-label="AutoInvent home">
          <img src="/icons/autoinvent-icon.svg" alt="AutoInvent" className="filing-rail-logo" />
        </button>
        {/* The intake autosaves and flushes on leaving the page, so exiting keeps the draft. */}
        <button type="button" className="filing-rail-exit" onClick={() => navigate('/dashboard')}>
          Save &amp; exit
        </button>
      </div>

      <ol className="filing-rail-steps">
        {PHASES.map((p) => {
          const state = p.id < phase ? 'done' : p.id === phase ? 'active' : 'todo'
          // The intake's parts come from the page; everything else is the static list above.
          const live = p.id === 1 && rail?.substeps?.length ? rail : null
          const parts = live ? live.substeps.map((s) => s.label) : p.parts || []
          const hasParts = parts.length > 0
          const isOpen = hasParts && open.has(p.id)

          return (
            <li key={p.id} className={`filing-rail-step is-${state}`}>
              {hasParts ? (
                <button
                  type="button"
                  className="filing-rail-row filing-rail-row--toggle"
                  onClick={() => toggle(p.id)}
                  aria-expanded={isOpen}
                >
                  <PhaseDot state={state} />
                  <span className="filing-rail-label">{p.label}</span>
                  <ChevronDown size={16} className={`filing-rail-chevron${isOpen ? ' is-open' : ''}`} aria-hidden="true" />
                </button>
              ) : (
                <span className="filing-rail-row">
                  <PhaseDot state={state} />
                  <span className="filing-rail-label">{p.label}</span>
                </span>
              )}

              {isOpen && (
                <ol className="filing-rail-substeps">
                  {parts.map((label, i) => {
                    let subState
                    let reachable = false
                    if (live) {
                      reachable = i <= (live.furthest ?? live.active)
                      // Ahead of the screen but already reached (they jumped back): still clickable.
                      subState = i < live.active ? 'done' : i === live.active ? 'active' : reachable ? 'visited' : 'todo'
                    } else {
                      subState = state === 'done' ? 'done' : 'todo'
                    }
                    return (
                      <li key={label} className={`filing-rail-sub is-${subState}`}>
                        {live ? (
                          <button
                            type="button"
                            className="filing-rail-sub-btn"
                            onClick={() => live.onSelect?.(i)}
                            disabled={!reachable || i === live.active}
                            aria-current={i === live.active ? 'step' : undefined}
                          >
                            <SubDot state={subState} />
                            {label}
                          </button>
                        ) : (
                          <span className="filing-rail-sub-btn is-static">
                            <SubDot state={subState} />
                            {label}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ol>
              )}
            </li>
          )
        })}
      </ol>

      {/* Permanent here rather than a dismissable banner over the question. */}
      <p className="filing-rail-privacy">
        <Lock size={15} aria-hidden="true" />
        <span>Your idea stays private, encrypted, never shared, and never used to train our AI.</span>
      </p>
    </aside>
  )
}

function PhaseDot({ state }) {
  return (
    <span className="filing-rail-dot" aria-hidden="true">
      {state === 'done' && <Check size={14} strokeWidth={3} />}
    </span>
  )
}

function SubDot({ state }) {
  return (
    <span className="filing-rail-subdot" aria-hidden="true">
      {state === 'done' && <Check size={11} strokeWidth={3} />}
    </span>
  )
}
